import {
  useGetFailedShiftAssignJobsQuery,
  useGetFailedShiftAssignRecordsQuery,
  useLazyDownloadShiftAssignTemplateQuery,
  useUploadShiftAssignMutation,
} from '@/app/features/employee/emp-attendance/emp-attendance.api';
import type { FailedShiftAssignRecord } from '@/app/features/employee/emp-attendance/emp-attendance.type';
import { BulkUploader, BulkUploadErrors } from '@/components/BulkUploader';
import useModal from '@/hooks/useModal';
import { toast } from '@/lib/toast';
import { Button, type TableProps } from 'antd';

type Props = {
  open: boolean;
  handleClose: (value?: boolean) => void;
};

const columns: TableProps<FailedShiftAssignRecord>['columns'] = [
  {
    title: 'Email',
    dataIndex: 'email',
    render: (val) =>
      val ? <span className="block min-w-[120px]">{val}</span> : 'n/a',
  },
  {
    title: 'Type',
    dataIndex: 'type',
    render: (val) => val ?? 'n/a',
  },
  {
    title: 'Shift Name',
    dataIndex: 'shift_name',
    render: (val) => val ?? 'n/a',
  },
  {
    title: 'Start Date',
    dataIndex: 'start_date',
    render: (val) => val ?? 'n/a',
  },
  {
    title: 'End Date',
    dataIndex: 'end_date',
    render: (val) => val ?? 'n/a',
  },
  {
    title: 'Failure Reason',
    dataIndex: 'failure_reason',
    render: (val) =>
      val ? (
        <span className="block min-w-[200px] text-red-400">{val}</span>
      ) : (
        'n/a'
      ),
  },
];

export default function ShiftAssignImport({ open, handleClose }: Props) {
  const [uploadShiftAssign, { isLoading: isUploading }] = useUploadShiftAssignMutation();
  const [downloadTemplate] = useLazyDownloadShiftAssignTemplateQuery();

  const {
    open: errorOpen,
    handleOpen: errorHandleOpen,
    handleClose: errorClose,
  } = useModal<undefined, 'ERROR_SHOW'>();

  const handleDownloadTemplate = async () => {
    try {
      const blobUrl = await downloadTemplate().unwrap();
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = 'shift-assignment-template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      toast('error', 'Failed to download template.');
    }
  };

  const handleUpload = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    await uploadShiftAssign(formData).unwrap();
  };

  return (
    <>
      <BulkUploader
        open={open}
        onClose={() => handleClose()}
        title="Shift Assignment Import"
        subtitle="File Type Must be in .xlsx"
        accept=".xlsx"
        onDownloadTemplate={handleDownloadTemplate}
        onUpload={handleUpload}
        uploadLoading={isUploading}
        errorViewer={
          <Button
            type="default"
            className="!h-10 text-center text-teal-500 text-sm font-normal underline !bg-transparent !border-none !shadow-none"
            onClick={() => errorHandleOpen({ type: 'ERROR_SHOW' })}
          >
            View Error Table Data
          </Button>
        }
      />

      <BulkUploadErrors<FailedShiftAssignRecord>
        open={errorOpen.open}
        onClose={errorClose}
        useGetJobsQuery={useGetFailedShiftAssignJobsQuery}
        useGetRecordsQuery={useGetFailedShiftAssignRecordsQuery}
        columns={columns}
        jobsErrorMessage="Failed to get shift assignment error data"
        recordsErrorMessage="Failed to get shift assignment error data"
      />
    </>
  );
}
